import { useEffect, useRef, useState } from "react";
import { findNearestImage, type MapillaryImage } from "../lib/mapillary";
import { useT } from "../i18n";

interface Props {
  lat: number;
  lng: number;
}

type State =
  | { kind: "loading" }
  | { kind: "found"; image: MapillaryImage; expanded: boolean }
  | { kind: "fallback" };

/** Keyless odkaz do Google Street View (Maps URL scheme, bez API klíče). */
function streetViewUrl(lat: number, lng: number): string {
  return `https://www.google.com/maps/@?api=1&map_action=pano&viewpoint=${lat},${lng}`;
}

/**
 * Street-level náhled okolí WC. Najde nejbližší Mapillary snímek a zobrazí
 * statický thumbnail; po kliknutí lazy-loadne interaktivní `mapillary-js`
 * viewer. Když snímek není (nebo chybí token), nabídne keyless Google odkaz.
 */
export default function StreetViewPreview({ lat, lng }: Props) {
  return <StreetViewPreviewForLocation key={`${lat},${lng}`} lat={lat} lng={lng} />;
}

function StreetViewPreviewForLocation({ lat, lng }: Props) {
  const t = useT();
  const [state, setState] = useState<State>({ kind: "loading" });
  const viewerHostRef = useRef<HTMLDivElement | null>(null);

  // Najdi nejbližší snímek při změně souřadnic; abort při zavření/změně.
  useEffect(() => {
    const controller = new AbortController();
    findNearestImage(lat, lng, { signal: controller.signal }).then((image) => {
      if (controller.signal.aborted) return;
      setState(image ? { kind: "found", image, expanded: false } : { kind: "fallback" });
    });
    return () => controller.abort();
  }, [lat, lng]);

  // Po rozbalení lazy-loadni mapillary-js a namountuj viewer.
  useEffect(() => {
    if (state.kind !== "found" || !state.expanded) return;
    const host = viewerHostRef.current;
    if (!host) return;

    let cancelled = false;
    let viewer: { remove: () => void } | null = null;
    const imageId = state.image.id;

    (async () => {
      try {
        const { Viewer } = await import("mapillary-js");
        await import("mapillary-js/dist/mapillary.css");
        if (cancelled) return;
        const token = import.meta.env.VITE_MAPILLARY_TOKEN;
        if (!token) return;
        viewer = new Viewer({ accessToken: token, container: host, imageId });
      } catch {
        // Selhání lazy-loadu: necháme prázdný kontejner, fallback řeší layout.
      }
    })();

    return () => {
      cancelled = true;
      viewer?.remove();
    };
  }, [state]);

  if (state.kind === "loading") {
    return <div className="streetview streetview-loading" aria-hidden="true" />;
  }

  if (state.kind === "fallback") {
    return (
      <a
        className="streetview-fallback"
        href={streetViewUrl(lat, lng)}
        target="_blank"
        rel="noreferrer"
      >
        {t("streetview.open")}
      </a>
    );
  }

  if (state.expanded) {
    return <div className="streetview streetview-viewer" ref={viewerHostRef} />;
  }

  return (
    <button
      type="button"
      className="streetview streetview-thumb"
      style={{ backgroundImage: `url(${state.image.thumbUrl})` }}
      onClick={() => setState({ ...state, expanded: true })}
      aria-label={t("streetview.expand")}
    >
      <span className="streetview-play" aria-hidden="true">▶</span>
    </button>
  );
}
